/**
 * record-values.ts — F22 Ścieżka C (widoki dodatkowe, PHASE_22_PLAN.md §8).
 *
 * Buduje `recordValues` (`ExecuteAdditionalViewDataInput.recordValues`,
 * `packages/shared/src/additional-views.ts`) dla wiązań `record_field` — mapa
 * slug pola → wartość JSON-safe (string/number/boolean/null), na żywo z
 * formularza (działa też w trybie tworzenia, przed zapisem rekordu).
 *
 * Dwa źródła są scalane pozycyjnie (późniejsze nadpisuje wcześniejsze — kolizja
 * slugów między ext-fields i core nie powinna wystąpić, ale kolejność jest
 * jawna na wypadek):
 *  - ext-fields (`extValues[slug]`) — działa WSZĘDZIE (dokumenty, kontrahent,
 *    pracownik), także w trybie create.
 *  - core fields — dla dokumentów przez `buildCoreFieldRecordValues`
 *    (`components/kartoteki/engine/engine-config.ts`); dla kontrahenta przez
 *    `buildContractorCoreRecordValues` (`components/contractors/ContractorDataTab.tsx`);
 *    dla pracownika przez `buildEmployeeCoreRecordValues`
 *    (`components/employees/EmployeeDataTab.tsx`).
 */

import type { ExecuteAdditionalViewDataInput } from '@saas/shared';

export type AdditionalViewRecordValues = ExecuteAdditionalViewDataInput['recordValues'];

/**
 * Koercja pojedynczej wartości do kształtu JSON-safe wymaganego przez
 * `recordValues` — string/number/boolean/null przechodzą wprost, wszystko inne
 * (tablice multiselect, obiekty, `undefined`) staje się `null` (parametr
 * `record_field` wskazujący na pole niescalarne nie ma sensownej wartości do
 * podstawienia w SQL — cichy `null` zamiast rzucenia błędu, spójne z resztą
 * silnika bindingów, który też nie waliduje typu w locie po stronie klienta).
 */
export function toAdditionalViewRecordValue(value: unknown): string | number | boolean | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  return null;
}

/**
 * Scala N źródeł (`extValues`, `coreRecordValues`, …) w jeden `recordValues`.
 * Źródła `null`/`undefined` są pomijane — wygodne dla callerów, którzy nie
 * zawsze mają wszystkie źródła dostępne (np. kontrahent/pracownik bez core map).
 */
export function buildAdditionalViewRecordValues(
  ...sources: Array<Record<string, unknown> | null | undefined>
): AdditionalViewRecordValues {
  const result: Record<string, string | number | boolean | null> = {};
  for (const src of sources) {
    if (!src) continue;
    for (const [slug, value] of Object.entries(src)) {
      result[slug] = toAdditionalViewRecordValue(value);
    }
  }
  return result;
}
